/**
 * TanStack Query hook for the admin metrics dashboard (STORY-M1-DASH).
 *
 * `useMetricsOverview` — `useQuery` over the BE-12 batched overview endpoint.
 * The range button maps to the backend `days` window (`rangeToDays`), and the
 * query is keyed by that resolved day-count (`["admin","metrics","overview",
 * days]`). One round trip feeds all 10 tiles. Read-only; 60s `staleTime`
 * (metrics are rolled-up aggregates, not live counters — a short cache avoids
 * a refetch storm on range toggling).
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  fetchMetricsOverview,
  metricsKeys,
  rangeToDays,
  type MetricsOverview,
  type MetricsRange,
} from "@/features/admin/dashboard/api";

export function useMetricsOverview(
  range: MetricsRange,
): UseQueryResult<MetricsOverview, Error> {
  const days = rangeToDays(range);
  return useQuery<MetricsOverview, Error>({
    queryKey: metricsKeys.overview(days),
    queryFn: () => fetchMetricsOverview(days),
    staleTime: 60_000,
  });
}
